// @file Content Picker store
import { trackEvent } from '@@/bits/analytics'
import { ContentPickerSource, getAiSearchSamplePrompt, getTranslatedAiSearchSamplePrompt } from '@@/bits/content_picker'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { getVuexStore } from '@@/bits/pinia'
import { safeLocalStorage } from '@@/bits/safe_storage'
import { vDel, vSet } from '@@/bits/vue'
import type {
  AiImageSearchResult,
  AudioSearchResult,
  ImageSearchResult,
  SearchResult,
  SearchResultDataCollectionMap,
  ThreeDObjectSearchResult,
  VideoSearchResult,
  WebSearchResult,
} from '@@/bits/web_search'
import { aiImageSearch, search as webSearch, searchByUrl, SearchFilters } from '@@/bits/web_search'
import type { Cid, Id } from '@@/types'
import type { DashboardSettingsState } from '@@/vuexstore/dashboard_settings'
import type { RootState } from '@@/vuexstore/surface/types'
import { defineStore } from 'pinia'
import { computed, reactive, ref, toRefs } from 'vue'

const CONTENT_SOURCE_HIDDEN_INPUT_MAP = {
  [ContentPickerSource.FileBrowser]: 'content-hidden-file-input',
  [ContentPickerSource.PhotoBooth]: 'content-hidden-image-input',
  [ContentPickerSource.VideoRecorder]: 'content-hidden-video-input',
} as const

export interface ContentPickerSearchMode {
  key: SearchFilters
  text: string
  visible: boolean
}

export enum SourceMenuScreen {
  SourceMenuList = 'SourceMenuList',
  SearchScreen = 'SearchScreen',
  AIImagePreview = 'AIImagePreview',
  AIImageSearchScreen = 'AIImageSearchScreen',
  StickerPicker = 'StickerPicker',
}

interface ContentPickerState {
  xContentSourceMenu: boolean
  contentSourceToManuallyTrigger: ContentPickerSource | null
  hiddenInputToManuallyTrigger:
    | (typeof CONTENT_SOURCE_HIDDEN_INPUT_MAP)[keyof typeof CONTENT_SOURCE_HIDDEN_INPUT_MAP]
    | null
  // It's possible to pick content for the same destination entity from different content pickers
  // context is used to determine the source of the content picker, e.g. if there are 2 content pickers in the same page, we can use this to differentiate them
  contentPickerContext: string
  activeContentSource: ContentPickerSource | null
  isOpenedFromSearch: boolean
  searchQueriesInProgress: string[]
  searchMoreQueriesInProgress: string[]
  error: any
  searchResults: Record<string, SearchResultDataCollectionMap>
  searchMoreUrls: Record<string, Record<SearchFilters, string>>
  searchFilter: SearchFilters | null
  defaultSearchMode: SearchFilters
  sourceMenuScreen: SourceMenuScreen
  searchQuery: string
  submittedSearchQuery: string
  searchSuggestions: string[]
  isInformingLastSearchNoResults: boolean
  // for link input, we still show the content picker but with a different placeholder text in the search bar and save the link as post attachment instead of searching
  isLinkInputMode: boolean
  searchBarPlaceholder: string
  hideBackButton: boolean
  xSearchModes: boolean
}

const blankState: ContentPickerState = {
  xContentSourceMenu: false,
  contentSourceToManuallyTrigger: null,
  hiddenInputToManuallyTrigger: null,
  contentPickerContext: '',
  activeContentSource: null,
  isOpenedFromSearch: false,
  searchQueriesInProgress: [],
  searchMoreQueriesInProgress: [],
  error: null,
  searchResults: {},
  searchMoreUrls: {},
  searchFilter: null,
  defaultSearchMode: SearchFilters.Web,
  sourceMenuScreen: SourceMenuScreen.SourceMenuList,
  searchQuery: '',
  submittedSearchQuery: '',
  searchSuggestions: [],
  isInformingLastSearchNoResults: false,
  /* for link input, we still show the content picker but with a different placeholder text in the search bar and save the link as post attachment instead of searching */
  isLinkInputMode: false,
  searchBarPlaceholder: '',
  hideBackButton: false,
  xSearchModes: true,
}

const SAFE_STORAGE_KEY = 'contentPickerSearchSuggestions'

export const useContentPickerStore = defineStore('contentPicker', () => {
  const vuexStore = getVuexStore<RootState | DashboardSettingsState>()

  const state = reactive({
    ...blankState,
  })

  const destinationEntityType = ref<string | undefined>(undefined)
  const destinationEntityUid = ref<Cid | Id | undefined>(undefined)

  /** Getters */
  const activeContentPickerPanel = computed<ContentPickerSource | null>(() => state.activeContentSource)
  const xContentPickerPanel = computed<boolean>(() => !!state.activeContentSource)
  const isInSearchScreen = computed<boolean>(() => state.sourceMenuScreen === SourceMenuScreen.SearchScreen)
  const isInAiImagePreview = computed<boolean>(() => state.sourceMenuScreen === SourceMenuScreen.AIImagePreview)
  const isInAiImageSearchScreen = computed<boolean>(
    () =>
      state.sourceMenuScreen === SourceMenuScreen.AIImageSearchScreen && state.searchFilter === SearchFilters.AiImages,
  )
  const isInAiImageSearchMode = computed<boolean>(() => isInAiImageSearchScreen.value || isInAiImagePreview.value)
  const isInStickersScreen = computed<boolean>(() => state.sourceMenuScreen === SourceMenuScreen.StickerPicker)
  const isInSourceMenuList = computed<boolean>(() => state.sourceMenuScreen === SourceMenuScreen.SourceMenuList)
  const isInAudioSearchScreen = computed<boolean>(() => state.searchFilter === SearchFilters.Audio)
  const isInWebSearchScreen = computed<boolean>(() => state.searchFilter === SearchFilters.Web)
  const xContentPicker = computed<boolean>(
    () => xContentPickerPanel.value || state.xContentSourceMenu || isInSearchScreen.value,
  )
  const currentSearchResultsMap = computed<SearchResultDataCollectionMap>(() => state.searchResults[state.searchQuery])
  const isSearchQueryInProgress = computed(() => state.searchQueriesInProgress.length > 0)
  const isSearchMoreQueryInProgress = computed(() => state.searchMoreQueriesInProgress.length > 0)
  const isError = computed<boolean>(() => !!state.error)
  const searchQueryShowingResult = computed(() => {
    if (state.searchFilter == null) return ''
    const resultForSearchQuery: any[] = state.searchResults[state.searchQuery]?.[state.searchFilter] ?? []
    if (state.searchQuery === state.submittedSearchQuery) {
      return state.searchQuery
    }
    if (resultForSearchQuery.length > 0) {
      return state.searchQuery
    }
    return state.submittedSearchQuery
  })
  // reverse copy of the search suggestions to show the most recent ones at the top
  const searchSuggestions = computed(() => [...new Set([...state.searchSuggestions].reverse())])
  const currentSearchResultsForFilter = computed<
    Array<WebSearchResult | AudioSearchResult | VideoSearchResult | ImageSearchResult | AiImageSearchResult>
  >(() => {
    if (state.searchFilter == null) return []
    const allResults = state.searchResults[searchQueryShowingResult.value]
    return allResults != null ? (allResults[state.searchFilter] ?? []) : []
  })
  const currentSearchMoreUrlForFilter = computed<string>(() => {
    if (state.searchFilter == null) return ''
    return state.searchMoreUrls[searchQueryShowingResult.value]?.[state.searchFilter]
  })
  const xSearchSuggestions = computed<boolean>(() => {
    /**
     * Cases where we show search suggestions:
     * - when user first open the screen and hasn't start a search.
     *    - currentSearchResultsForFilter is empty
     * - when user searched but deleted all the text/press clear button.
     *    - currentSearchResultsForFilter will reset when query becomes empty
     *    - @see setSearchQuery
     * - when user searched a query with no results and changed it immediately
     *    - we utilize isInformingLastSearchNoResults
     ***/
    if (state.searchSuggestions.length === 0) return false
    if (isSearchQueryInProgress.value) return false
    if (currentSearchResultsForFilter.value.length !== 0) return false
    if (state.isInformingLastSearchNoResults) return false
    return true
  })

  const isImageSearchMode = computed(() => {
    return (
      state.searchFilter === SearchFilters.Images ||
      state.searchFilter === SearchFilters.Gif ||
      state.searchFilter === SearchFilters.UnsplashImages ||
      state.searchFilter === SearchFilters.Stock ||
      isInAiImageSearchMode.value
    )
  })

  /** Actions */

  // Ensure that we set these values as a pair
  function setAttachmentDestination(type: string, uid: string): void {
    destinationEntityType.value = type
    destinationEntityUid.value = uid
  }

  function pickContentSource({ source }: { source: ContentPickerSource }): void {
    endLinkInputMode()
    state.activeContentSource = source
    state.xContentSourceMenu = false
  }

  function showContentSourceMenu({
    search,
    searchFilter: updatedSearchFilter,
    contentPickerContext,
    destinationEntityType,
    destinationEntityUid,
  }: {
    search?: boolean
    searchFilter?: SearchFilters
    contentPickerContext?: string
    destinationEntityType?: string
    destinationEntityUid?: string
  } = {}): void {
    state.xContentSourceMenu = true
    if (search) {
      state.isOpenedFromSearch = true
      state.sourceMenuScreen = SourceMenuScreen.SearchScreen
      state.searchFilter = updatedSearchFilter ?? state.defaultSearchMode
    }
    if (contentPickerContext) {
      state.contentPickerContext = contentPickerContext
    }
    if (destinationEntityType != null && destinationEntityUid != null) {
      setAttachmentDestination(destinationEntityType, destinationEntityUid)
    }
  }

  function startLinkInputMode(): void {
    state.isLinkInputMode = true
  }

  function setSourceMenuScreen(screen: SourceMenuScreen): void {
    state.sourceMenuScreen = screen
  }

  function resetSearch(): void {
    let searchFilter = state.searchFilter
    if (searchFilter === SearchFilters.AiImages) {
      searchFilter = SearchFilters.Images
    }

    const newState = reactive({ ...blankState, searchFilter })

    Object.assign(state, newState)
  }

  function clearSearchFilter(): void {
    state.searchFilter = null
  }

  function setSearchBarPlaceholder(placeholder: string): void {
    state.searchBarPlaceholder = placeholder
  }

  function setSearchQuery({ query }: { query: string }): void {
    state.searchQuery = query
    state.isInformingLastSearchNoResults = false
    if (query.trim() === '') {
      state.submittedSearchQuery = ''
    }
  }

  const appendSearchSuggestions = ({ query }: { query: string }): void => {
    const stringValue = safeLocalStorage.getItem(SAFE_STORAGE_KEY)
    const currentSuggestions: string[] = stringValue ? JSON.parse(stringValue) : []
    let newSuggestions = currentSuggestions.filter((q) => q !== query)
    newSuggestions.push(query)
    newSuggestions = newSuggestions.slice(-10)
    safeLocalStorage.setItem(SAFE_STORAGE_KEY, asciiSafeStringify(newSuggestions))
    state.searchSuggestions = newSuggestions
  }

  const startSearch = ({ query, more }: { query: string; more?: boolean }): void => {
    state.submittedSearchQuery = state.searchQuery

    const shouldSearchMore = more ?? false
    if (shouldSearchMore) {
      state.searchMoreQueriesInProgress = [...state.searchMoreQueriesInProgress, state.searchQuery]
    } else {
      state.searchQueriesInProgress = [...state.searchQueriesInProgress, query]
    }
  }

  const handleSearchResult = ({
    result,
    filter,
    query,
    mode = 'update',
  }: {
    result: SearchResult
    filter: SearchFilters
    query: string
    mode?: 'update' | 'append'
  }): void => {
    let searchHits: any[] = []
    switch (filter) {
      case SearchFilters.Web:
        searchHits = result.data.webpages as WebSearchResult[]
        break
      case SearchFilters.Audio:
        searchHits = result.data.audio as AudioSearchResult[]
        break
      case SearchFilters.Videos:
        searchHits = result.data.videos as VideoSearchResult[]
        break
      case SearchFilters.UnsplashImages:
      case SearchFilters.Images:
      case SearchFilters.Gif:
      case SearchFilters.Stock:
        searchHits = result.data.images as ImageSearchResult[]
        break
      case SearchFilters.AiImages:
        searchHits = result.data.images as AiImageSearchResult[]
        break
      case SearchFilters.ThreeDObjects:
        searchHits = result.data.models as ThreeDObjectSearchResult[]
        break
    }
    if (searchHits.length === 0) {
      state.isInformingLastSearchNoResults = true
    }

    const currentSearchResults = state.searchResults[query] ?? {}

    if (mode === 'update') {
      vSet(state.searchResults, query, {
        ...currentSearchResults,
        [filter]: searchHits,
      })
    } else {
      vSet(state.searchResults, query, {
        ...currentSearchResults,
        [filter]: [...(currentSearchResults[filter] ?? []), ...searchHits],
      })
    }
    if (result.links?.next && searchHits.length > 0) {
      vSet(state.searchMoreUrls, query, { ...state.searchMoreUrls[query], [filter]: result.links.next })
    } else {
      vSet(state.searchMoreUrls, query, { ...state.searchMoreUrls[query], [filter]: undefined })
    }
  }

  const handleSearchError = ({ filter, error }: { filter?: SearchFilters; error: any }): void => {
    if (filter === state.searchFilter) {
      state.error = error
    } else {
      // do nothing, error belongs to the different tab
    }
  }

  const finishSearch = ({ query }: { query: string }): void => {
    if (state.searchQueriesInProgress.includes(query)) {
      vDel(state.searchQueriesInProgress, state.searchQueriesInProgress.indexOf(query))
    }
    if (state.searchMoreQueriesInProgress.includes(query)) {
      vDel(state.searchMoreQueriesInProgress, state.searchMoreQueriesInProgress.indexOf(query))
    }
  }

  function pickSearchFilter({ filter }: { filter: SearchFilters }): void {
    if (filter !== SearchFilters.AiImages) {
      endLinkInputMode()
    }

    if (filter && filter !== SearchFilters.AiImages) {
      state.sourceMenuScreen = SourceMenuScreen.SearchScreen
    }

    state.submittedSearchQuery = state.searchQuery
    state.searchFilter = filter
    state.error = null
  }

  async function search(options?: { filter?: SearchFilters; query?: string }): Promise<void> {
    let searchResult: SearchResult | null = null
    if (state.searchFilter == null) {
      pickSearchFilter({ filter: options?.filter ?? SearchFilters.Web })
    }
    const filter: SearchFilters = options?.filter ?? state.searchFilter ?? state.defaultSearchMode
    const query: string = options?.query ?? state.searchQuery

    appendSearchSuggestions({ query })
    try {
      startSearch({ query })
      if (filter === SearchFilters.AiImages) {
        state.sourceMenuScreen = SourceMenuScreen.AIImageSearchScreen

        /**
         * If the user is searching for the sample prompt,
         * we don't want to use the translated version so that
         * results are consistent.
         */
        if (query === getTranslatedAiSearchSamplePrompt()) {
          // TODO: [to-be-migrated][user]
          searchResult = await aiImageSearch(getAiSearchSamplePrompt(), vuexStore?.state.user?.id)
        } else {
          // TODO: [to-be-migrated][user]
          searchResult = await aiImageSearch(query, vuexStore?.state.user?.id)
        }
        if (searchResult.error) {
          throw new Error(searchResult.error)
        }
      } else {
        searchResult = await webSearch(query, vuexStore?.state.user?.id, filter)
      }

      if (searchResult !== null) {
        handleSearchResult({ result: searchResult, query, filter, mode: 'update' })
      }
    } catch (error) {
      handleSearchError({ filter, error })
    } finally {
      finishSearch({ query })
    }
  }

  function hideContentPickerPanel(): void {
    state.activeContentSource = null
    resetSearch()
  }

  function hideContentSourceMenu(): void {
    state.xContentSourceMenu = false
    resetSearch()
  }

  function resetContentPicker(): void {
    state.contentPickerContext = ''
    state.searchFilter = null
    hideContentPickerPanel()
    hideContentSourceMenu()
    setSourceMenuScreen(SourceMenuScreen.SourceMenuList)
    destinationEntityType.value = undefined
    destinationEntityUid.value = undefined
  }

  async function searchMore(options?: { query: string }): Promise<void> {
    let searchResult: SearchResult | null = null
    const filter: SearchFilters = state.searchFilter ?? state.defaultSearchMode
    const query: string = options?.query ?? state.searchQuery
    trackEvent('Content Picker', 'Search more', null, null, {
      searchQuery: query,
      selectedSearchFilter: filter,
    })
    try {
      startSearch({ query, more: true })
      const currentFilter: SearchFilters = filter
      const url = state.searchMoreUrls[query][currentFilter]
      if (url) searchResult = await searchByUrl(url) // For searching using next url
      if (searchResult != null) {
        handleSearchResult({ result: searchResult, filter, query, mode: 'append' })
      }
    } catch (error) {
      handleSearchError({ error })
    } finally {
      finishSearch({ query })
    }
  }

  function clearSearchError(): void {
    state.error = null
  }

  function syncSearchSuggestionsWithStorage(): void {
    const stringValue = safeLocalStorage.getItem(SAFE_STORAGE_KEY)
    const suggestions: string[] = stringValue ? JSON.parse(stringValue) : []
    state.searchSuggestions = suggestions
  }

  function endLinkInputMode(): void {
    state.isLinkInputMode = false
  }

  function setDefaultSearchMode(mode: SearchFilters): void {
    state.defaultSearchMode = mode
  }

  const { searchSuggestions: _unreversedSuggestions, ...stateToBeExported } = toRefs(state)

  /** Manual hidden input trigger */
  function triggerHiddenInput(contentSource: ContentPickerSource): void {
    state.contentSourceToManuallyTrigger = contentSource
    state.hiddenInputToManuallyTrigger = CONTENT_SOURCE_HIDDEN_INPUT_MAP[contentSource]
  }

  // z-index override
  const zIndexOverride = ref<number | null>(null)
  const setZIndexOverride = (value: number | null): void => {
    zIndexOverride.value = value
  }

  function disableBackButton(): void {
    state.hideBackButton = true
  }

  function hideSearchModes(): void {
    state.xSearchModes = false
  }

  function showSearchModes(): void {
    state.xSearchModes = true
  }

  return {
    // state
    ...stateToBeExported,
    destinationEntityType,
    destinationEntityUid,
    // getters
    activeContentPickerPanel,
    xContentPicker,
    xContentPickerPanel,
    isInSearchScreen,
    isInAiImagePreview,
    isInAiImageSearchScreen,
    isInAiImageSearchMode,
    isImageSearchMode,
    isInStickersScreen,
    isInSourceMenuList,
    isInAudioSearchScreen,
    isInWebSearchScreen,
    currentSearchResultsMap,
    isSearchQueryInProgress,
    isSearchMoreQueryInProgress,
    isError,
    currentSearchResultsForFilter,
    currentSearchMoreUrlForFilter,
    xSearchSuggestions,
    searchSuggestions,

    // actions
    setAttachmentDestination,
    pickContentSource,
    showContentSourceMenu,
    setSourceMenuScreen,
    startLinkInputMode,
    clearSearchFilter,
    setSearchBarPlaceholder,
    setSearchQuery,
    search,
    hideContentPickerPanel,
    hideContentSourceMenu,
    pickSearchFilter,
    searchMore,
    clearSearchError,
    syncSearchSuggestionsWithStorage,
    endLinkInputMode,
    setDefaultSearchMode,
    resetContentPicker,
    triggerHiddenInput,
    handleSearchResult,

    // z-index override
    zIndexOverride,
    setZIndexOverride,
    disableBackButton,
    hideSearchModes,
    showSearchModes,
  }
})
